import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Share, Plus, Smartphone, X } from "lucide-react";
import { db } from "@/lib/storage";

const DISMISS_KEY = "notaku:a2hs-dismissed";
const MIN_NOTES = 3;

interface BIPEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Prompt Add-to-Home-Screen kontekstual (muncul setelah 3 nota). */
export function A2HSPrompt() {
  const { data: notes = [] } = useQuery({ queryKey: ["notes"], queryFn: () => db.getNotes() });
  const [bip, setBip] = useState<BIPEvent | null>(null);
  const [open, setOpen] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Sudah installed?
    if (window.matchMedia?.("(display-mode: standalone)").matches) return;
    // Sudah di-dismiss <14 hari?
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - at < 14 * 24 * 3600 * 1000) return;

    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !("MSStream" in window);
    if (isIOS) setIosMode(true);

    const handler = (e: Event) => {
      e.preventDefault();
      setBip(e as BIPEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (notes.length >= MIN_NOTES && (bip || iosMode)) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [notes.length, bip, iosMode]);

  function dismiss() {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setOpen(false);
  }
  async function accept() {
    if (bip) {
      await bip.prompt();
      await bip.userChoice.catch(() => undefined);
    }
    dismiss();
  }

  if (!open) return null;
  return (
    <div className="fixed inset-x-0 bottom-24 z-40 px-4 pointer-events-none">
      <div className="pointer-events-auto max-w-sm mx-auto rounded-2xl bg-card border border-border shadow-pop p-4 relative">
        <button onClick={dismiss} aria-label="Tutup" className="tap absolute top-2 right-2 p-1 rounded-full text-muted-foreground hover:bg-accent">
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-primary/10 text-primary p-2"><Smartphone className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-medium">Simpan Notaku di layar utama</div>
            <div className="t-caption mt-0.5">Biar data aman & buka lebih cepat, seperti aplikasi biasa.</div>
            {iosMode ? (
              <div className="mt-3 text-sm space-y-1.5">
                <div className="flex items-center gap-1.5">1. Ketuk <Share className="h-4 w-4 inline" /> di Safari</div>
                <div className="flex items-center gap-1.5">2. Pilih <Plus className="h-4 w-4 inline" /> "Tambah ke Layar Utama"</div>
                <button onClick={dismiss} className="tap mt-2 rounded-full bg-card border border-border px-3 py-1.5 text-sm">Nanti aja</button>
              </div>
            ) : (
              <div className="mt-3 flex gap-2">
                <button onClick={accept} className="tap rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium">Pasang</button>
                <button onClick={dismiss} className="tap rounded-full bg-card border border-border px-3 py-1.5 text-sm">Nanti</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
